import {Box, Button, Stack, TextField, Typography} from "@mui/material";
import React, {useEffect, useMemo, useState} from 'react';
import {adminConfigurationService, DEFAULT_SHAREPOINT_FORM_VALUES} from "../../../../../services/admin-config.service";
import {SharePointPropertiesData} from "../../../../../models/admin-config.models";
import CircularProgress from "@mui/material/CircularProgress";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "../../../../../contexts/SnackbarContext";
import {useForm} from "react-hook-form";
import {
    SharePointConfigurationFormWrapper,
    StyledFormButtons,
    StyledFormFields
} from "./SharePointConfiguration.styled";

const SharePointConfiguration = () => {
    const {t} = useTranslation();
    const { showSnackbar } = useSnackbar();

    const [isFetching, setIsFetching] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { register, reset, handleSubmit, formState: { errors, isDirty } } = useForm<SharePointPropertiesData>({
        defaultValues: DEFAULT_SHAREPOINT_FORM_VALUES,
    });

    const fetchConfiguration = async () => {
        setIsFetching(true);

        try {
            const data = await adminConfigurationService.fetchSharePointProperties();
            reset(data);
        } catch (e) {
            console.error(e);
            showSnackbar(t('configuration.sharepointConfig.fetchDataError'), 'error');
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        void fetchConfiguration();
    }, []);

    const onSubmit = async (formData: SharePointPropertiesData) => {
        setIsSubmitting(true);

        try {
            await adminConfigurationService.setSharePointProperties(formData);
            reset(formData);

            showSnackbar(t('configuration.sharepointConfig.saveDataSuccess'), 'success');

            await fetchConfiguration();
        } catch (e) {
            console.error(e);
            showSnackbar(t('configuration.sharepointConfig.saveDataError'), 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formFields = useMemo(() => [
        {
            name: 'tenantId' as keyof SharePointPropertiesData,
            label: t('configuration.sharepointConfig.tenant.label'),
            placeholder: t('configuration.sharepointConfig.tenant.placeholder'),
            helperText: t('configuration.sharepointConfig.tenant.helper'),
            rules: { required: t('configuration.sharepointConfig.validation') },
        },
        {
            name: 'maxFileSizeBytes' as keyof SharePointPropertiesData,
            label: t('configuration.sharepointConfig.maxFileSizeBytes.label'),
            placeholder: t('configuration.sharepointConfig.maxFileSizeBytes.placeholder'),
            helperText: t('configuration.sharepointConfig.maxFileSizeBytes.helper'),
            rules: { required: t('configuration.sharepointConfig.validation') },
        },
        {
            name: 'clientId' as keyof SharePointPropertiesData,
            label: t('configuration.sharepointConfig.client.label'),
            placeholder: t('configuration.sharepointConfig.client.placeholder'),
            helperText: t('configuration.sharepointConfig.client.helper'),
            rules: { required: t('configuration.sharepointConfig.validation') },
        },
        {
            name: 'clientSecret' as keyof SharePointPropertiesData,
            label: t('configuration.sharepointConfig.clientSecret.label'),
            placeholder: t('configuration.sharepointConfig.clientSecret.placeholder'),
            helperText: t('configuration.sharepointConfig.clientSecret.helper'),
            rules: { required: t('configuration.sharepointConfig.validation') },
        },
        {
            name: 'siteId' as keyof SharePointPropertiesData,
            label: t('configuration.sharepointConfig.site.label'),
            placeholder: t('configuration.sharepointConfig.site.placeholder'),
            helperText: t('configuration.sharepointConfig.site.helper'),
            rules: { required: t('configuration.sharepointConfig.validation') },
        },
        {
            name: 'driveId' as keyof SharePointPropertiesData,
            label: t('configuration.sharepointConfig.drive.label'),
            placeholder: t('configuration.sharepointConfig.drive.placeholder'),
            helperText: t('configuration.sharepointConfig.drive.helper'),
            rules: { required: t('configuration.sharepointConfig.validation') },
        },
    ], [t]);

    return (
        <Stack className="sharepoint-configuration"
               id="sharepoint-config-form"
               component="form"
               onSubmit={handleSubmit(onSubmit)}
               direction="column"
               spacing={4}
        >
            <SharePointConfigurationFormWrapper>
                <StyledFormFields>

                    {(isFetching || isSubmitting) && (
                        <Box display="flex" justifyContent="center" alignItems="center" textAlign="center" height="200px">
                            <CircularProgress size={36} />
                        </Box>
                    )}

                    {(!isFetching) && (
                        <Stack direction="column" spacing={4} display="flex">

                            {formFields.map((field) => (
                                <Box key={field.name}>
                                    <Typography variant="body2" sx={{color: 'text.hint'}}>
                                        {field.label}
                                    </Typography>
                                    <TextField {...register(field.name, field.rules)}
                                               error={!!errors[field.name]}
                                               placeholder={field.placeholder}
                                               helperText={field.helperText}
                                               type='text'
                                               fullWidth
                                               sx={{maxWidth: {xs: '100%', mdPlus: '500px'}}}
                                    />
                                </Box>
                            ))}

                        </Stack>
                    )}
                </StyledFormFields>

                <StyledFormButtons>
                    <Button
                        type="submit"
                        form="sharepoint-config-form"
                        variant="contained"
                        disabled={isFetching || isSubmitting || !isDirty}
                    >
                        {isSubmitting ? <CircularProgress size={16} /> : t('common.save')}
                    </Button>
                </StyledFormButtons>
            </SharePointConfigurationFormWrapper>
        </Stack>
    );
};

export default SharePointConfiguration;